// ─────────────────────────────────────────────────────────────────────────────
// Buckly's Session API (B-4.2): GET /api/buckly/session (and ?open=<id> for a History row), and the
// POSTs for Matt's presses only (begin, end, tick, confirm, reject, close) and a typed session turn,
// read the house way (axios, apiBase, authToken — todayApi.ts). The server answers each panel with its
// data, or "no_manual" while Matt's manual for it is not current in her store; a panel with no data
// keeps the design's drawn panel. A read that fails answers null, and nothing is drawn from it. A press
// the server refuses (409) writes nothing and answers { ok: false }.
// ─────────────────────────────────────────────────────────────────────────────

import axios from 'axios';
import { useEffect, useState } from 'react';
import { apiBase, authToken } from '../../api/apiBase';

export type NoManual = 'no_manual';

export interface SessionRecord {
  session_id: string; day: string; number_in_day: number;
  type: 'deep_session' | 'debrief' | 'reset_after_a_loss'; surface: 'voice' | 'typed';
  started_at: string; ended_at: string; status: 'open' | 'ended' | 'not_ended';
}
export interface ServedRef { source: string; folder?: string; ref: string; title: string; placeholder?: boolean }
export interface TranscriptEntry { time: string; speaker: 'matt' | 'buckly'; text: string; surface: 'voice' | 'typed'; served_refs: ServedRef[] }
export interface FocusItem { kind: 'commitment_missed' | 'todays_action' | 'arthur_journal'; text: string; source_ref: string; why: string }
export interface Ticked { kind: string; source_ref: string }
export interface DrawLine { title: string; source_ref: string; grade: "PROVEN" | 'PARTIAL' | "Arthur's"; line: string }
export interface Progress {
  id: string; kind: string; check: string; kept: number | null; of: number | null; since: string;
  state: 'green' | 'red' | 'grey' | 'unavailable'; line: string; missed_days: string[];
}
export interface Commitment {
  id: string; session_id: string; made_on: string; kind: string; text: string; number: number | null;
  status: 'open' | 'closed'; closed_on: string; progress: Progress;
}
export interface Proposal { index: number; kind: string; text: string; number: number | null; check: string; confirmed: boolean }
export interface Summary {
  session_id: string; summary: string; key_insight: string; next_focus: string;
  proposed_commitments: { kind: string; text: string; number: number | null }[];
}
export interface HistoryRow {
  session_id: string; day: string; number_in_day: number; type: SessionRecord['type']; started_at: string;
  ended_at: string; length: number | null; focus: string; outcome: string; commitments_kept: string; status: SessionRecord['status'];
}

export interface BucklySession {
  room: { status_line: string; open_session: SessionRecord | null; types: string[]; gaps: string[] } | NoManual;
  focus: { items: FocusItem[]; ticked: Ticked[]; gaps: string[]; empty_line: string } | NoManual;
  flow: { phases: string[]; empty_line: string } | NoManual;
  live: { session: SessionRecord | null; transcript: TranscriptEntry[] } | NoManual;
  drawing: { lists: { deep_expertise: DrawLine[]; learned_memory: DrawLine[]; arthurs_data: DrawLine[] }; empty_line: string } | NoManual;
  technique: { technique: { title: string; steps: string[]; source_ref: string } | null; empty_line: string } | NoManual;
  commitments: { from_this_session: { session_id: string | null; proposals: Proposal[] }; open: Commitment[] } | NoManual;
  summary: { summary: Summary | null; session_id: string | null; dream_line: string } | NoManual;
  history: { rows: HistoryRow[]; footer: string } | NoManual;
  header: { status_line: string } | NoManual;
  opened?: { session: SessionRecord; transcript: TranscriptEntry[]; summary: Summary | null } | null | NoManual;
}

export type Press = 'begin' | 'end' | 'tick' | 'confirm' | 'reject' | 'close' | 'turn';

/** One panel's data, or null while its manual is not current (or nothing was read). */
export function sessionPanel<K extends keyof BucklySession>(api: BucklySession | null | undefined, key: K):
  Exclude<BucklySession[K], NoManual | undefined | null> | null {
  const value = api ? api[key] : null;
  if (!value || value === 'no_manual' || typeof value !== 'object') return null;
  return value as Exclude<BucklySession[K], NoManual | undefined | null>;
}

const headers = (token?: string | null) => ({ Authorization: `Bearer ${token || authToken()}`, 'Cache-Control': 'no-cache' });

export async function readSession(token?: string | null, open?: string | null): Promise<BucklySession | null> {
  try {
    const response = await axios.get(`${apiBase()}/buckly/session`, {
      headers: headers(token), ...(open ? { params: { open } } : {}),
    });
    return response.data && typeof response.data === 'object' ? (response.data as BucklySession) : null;
  } catch {
    return null;
  }
}

/** One of Matt's presses (or a typed session turn). Refused (409) or failed: { ok: false }, nothing written. */
export async function pressSession(press: Press, body: Record<string, unknown>, token?: string | null):
  Promise<{ ok: boolean; data: unknown }> {
  try {
    const response = await axios.post(`${apiBase()}/buckly/session/${press}`, body, { headers: headers(token) });
    return { ok: true, data: response.data };
  } catch (error) {
    const data = (error as { response?: { data?: unknown } }).response?.data;
    return { ok: false, data: data ?? null };
  }
}

/** Her Session, read when the caller mounts and again whenever `refresh` or `open` changes. */
export function useBucklySession(token?: string | null, refresh?: unknown, open?: string | null): BucklySession | null {
  const [session, setSession] = useState<BucklySession | null>(null);
  useEffect(() => {
    let live = true;
    readSession(token, open).then((got) => { if (live) setSession(got); });
    return () => { live = false; };
  }, [token, refresh, open]);
  return session;
}
